import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Download,
  Loader2,
  Film,
  Trash2,
  Play,
  Pause,
  Wand2,
  Sparkles,
  RotateCcw,
  X,
  Plus,
  Pencil,
  MessageSquare,
  Send,
  Type,
  FolderOpen,
  Palette,
  Music,
  Upload,
  Volume2,
  Save,
  BookOpen,
  Eye,
  EyeOff,
} from "lucide-react";
import VideoUpload from "./video-upload";
import EditingTimeline from "./editing-timeline";
import VideoPreviewOverlay from "./video-preview-overlay";
import { type Subtitle } from "./subtitle-editor";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import type { MgStyleTemplate, MgCaptionStyle, MgScenePlanTemplate } from "@shared/schema";
import { useRotatingMessage } from "@/components/ui/ai-generating-overlay";

interface SfxFile {
  id: string;
  fileName: string;
  startTime: number;
  duration: number;
  volume: number;
  path: string;
}

interface ScenePlan {
  captionStyle: string;
  overlays: Array<{
    type: string;
    startTime: number;
    endTime: number;
    payload: Record<string, unknown>;
  }>;
}

interface TranscriptionWord {
  word: string;
  start: number;
  end: number;
}

interface TranscriptionData {
  fullText: string;
  words: TranscriptionWord[];
  duration: number;
  language: string;
}

interface SchemaOverlayAnchor {
  startWord: string;
  startOccurrence: number;
  endWord: string;
  endOccurrence: number;
}

interface ScenePlanTemplateOverlayWithAnchor {
  type: string;
  startRatio: number;
  endRatio: number;
  payload: Record<string, unknown>;
  anchor?: SchemaOverlayAnchor;
}

const CAPTION_STYLES: Array<{ value: string; label: string }> = [
  { value: "karaoke", label: "Karaoke" },
  { value: "word-by-word", label: "1 słowo na raz" },
  { value: "fade", label: "Zanikanie" },
  { value: "bold-impact", label: "Bold Impact" },
  { value: "slide-up", label: "Wjazd" },
  { value: "pop", label: "Pop" },
  { value: "kinetic-bounce", label: "Kinetic" },
  { value: "glow", label: "Neon" },
  { value: "motion-card", label: "Motion Card" },
];

type EditorStep = "upload" | "editor";

export interface CaptionCustomStyle {
  bgColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  textTransform: "none" | "uppercase" | "lowercase";
  dropShadow: boolean;
  shadowColor: string;
  shadowBlur: number;
  entranceEffect: "none" | "fade-in" | "scale-up" | "slide-up" | "bounce-in";
}

const DEFAULT_CAPTION_CUSTOM: CaptionCustomStyle = {
  bgColor: "rgba(0,0,0,0.7)",
  textColor: "#ffffff",
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: "1.15em",
  fontWeight: 900,
  textTransform: "uppercase",
  dropShadow: true,
  shadowColor: "rgba(0,0,0,0.9)",
  shadowBlur: 8,
  entranceEffect: "none",
};

const FONT_OPTIONS = [
  "Arial, Helvetica, sans-serif",
  "Impact, Haettenschweiler, sans-serif",
  "Georgia, serif",
  "Courier New, monospace",
  "Verdana, Geneva, sans-serif",
  "Trebuchet MS, sans-serif",
  "Palatino Linotype, serif",
];

const ENTRANCE_EFFECTS: Array<{ value: CaptionCustomStyle["entranceEffect"]; label: string }> = [
  { value: "none", label: "Brak" },
  { value: "fade-in", label: "Wjazdowe" },
  { value: "scale-up", label: "Powiększenie" },
  { value: "slide-up", label: "Wsuwanie" },
  { value: "bounce-in", label: "Sprężysty" },
];

class VideoEditorSectionBoundary extends React.Component<
  { children: React.ReactNode; label: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; label: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { children: React.ReactNode; label: string }): void {
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="w-full h-full flex items-center justify-center rounded-lg border border-red-500/20 bg-red-500/5 text-red-300 text-xs px-4 text-center">
        Błąd sekcji: {this.props.label}. Odśwież moduł lub kliknij Nowy.
      </div>
    );
  }
}
const DYNAMIC_TEXT_TOKEN = "__DYNAMIC_TEXT__";
const DYNAMIC_LINE_1_TOKEN = "__DYNAMIC_LINE_1__";
const DYNAMIC_LINE_2_TOKEN = "__DYNAMIC_LINE_2__";

function normalizeWord(word: string): string {
  return word
    .toLowerCase()
    .replace(/[.,!?;:()\[\]{}"'`]/g, "")
    .trim();
}

function findClosestWordIndex(words: TranscriptionWord[], time: number): number {
  const firstAfterIndex = words.findIndex((w) => w.start >= time);
  if (firstAfterIndex <= 0) {
    return firstAfterIndex === -1 ? Math.max(0, words.length - 1) : firstAfterIndex;
  }

  const prev = words[firstAfterIndex - 1];
  const next = words[firstAfterIndex];
  const prevDelta = Math.abs(time - prev.start);
  const nextDelta = Math.abs(next.start - time);
  return prevDelta <= nextDelta ? firstAfterIndex - 1 : firstAfterIndex;
}

function countWordOccurrence(
  words: TranscriptionWord[],
  normalizedWord: string,
  upToIndex: number
): number {
  return words.slice(0, upToIndex + 1).reduce((count, currentWord) => {
    const isMatch = normalizeWord(currentWord.word) === normalizedWord;
    return isMatch ? count + 1 : count;
  }, 0);
}

function findWordByOccurrence(
  words: TranscriptionWord[],
  normalizedWord: string,
  occurrence: number
): TranscriptionWord | null {
  if (!normalizedWord || occurrence <= 0) return null;

  let seenCount = 0;
  const match = words.find((word) => {
    const isMatch = normalizeWord(word.word) === normalizedWord;
    if (!isMatch) return false;
    seenCount += 1;
    return seenCount === occurrence;
  });

  return match ?? null;
}

function sanitizeCustomCodeBackdrop(source: string): string {
  if (!source) return source;

  return source
    .replace(
      /backgroundColor\s*:\s*['\"]#000(?:000)?['\"]\s*,?/gi,
      "backgroundColor: 'transparent',"
    )
    .replace(/backgroundColor\s*:\s*['\"]black['\"]\s*,?/gi, "backgroundColor: 'transparent',")
    .replace(
      /backgroundColor\s*:\s*['\"]rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*1(?:\.0+)?\s*\)['\"]\s*,?/gi,
      "backgroundColor: 'transparent',"
    )
    .replace(/background\s*:\s*['\"]#000(?:000)?['\"]\s*,?/gi, "background: 'transparent',")
    .replace(/background\s*:\s*['\"]black['\"]\s*,?/gi, "background: 'transparent',");
}

function sanitizeCustomCodePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const code =
    typeof payload.code === "string" ? sanitizeCustomCodeBackdrop(payload.code) : payload.code;
  const compiled =
    typeof payload.compiled === "string"
      ? sanitizeCustomCodeBackdrop(payload.compiled)
      : payload.compiled;
  return { ...payload, code, compiled };
}

function escapeForDoubleQuotedString(rawText: string): string {
  return rawText.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, " ").trim();
}

function replaceDynamicTextToken(source: string, text: string): string {
  if (!source || !source.includes(DYNAMIC_TEXT_TOKEN)) return source;
  const escapedText = escapeForDoubleQuotedString(text);
  return source.replaceAll(DYNAMIC_TEXT_TOKEN, escapedText);
}

function splitSubtitleToTwoLines(text: string): { line1: string; line2: string } {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { line1: text.trim(), line2: "" };

  const splitIndex = Math.ceil(words.length / 2);
  const line1 = words.slice(0, splitIndex).join(" ");
  const line2 = words.slice(splitIndex).join(" ");
  return { line1, line2 };
}

function replaceTwoLineTokens(source: string, text: string): string {
  if (!source) return source;
  const { line1, line2 } = splitSubtitleToTwoLines(text);
  const escapedLine1 = escapeForDoubleQuotedString(line1 || text);
  const escapedLine2 = escapeForDoubleQuotedString(line2 || line1 || text);

  return source
    .replaceAll(DYNAMIC_LINE_1_TOKEN, escapedLine1)
    .replaceAll(DYNAMIC_LINE_2_TOKEN, escapedLine2);
}

function looksLikeTitleSubtitleTemplate(code: string): boolean {
  if (!code) return false;
  const hasTitle = code.includes("TITLE_TEXT");
  const hasSubtitle = code.includes("SUBTITLE_TEXT");
  const hasSubtitleLetters = code.includes("subtitleLetters");
  return hasTitle && hasSubtitle && hasSubtitleLetters;
}

function resolveOverlayTimesFromAnchor(
  overlay: ScenePlanTemplateOverlayWithAnchor,
  words: TranscriptionWord[],
  videoDuration: number
): { startTime: number; endTime: number } {
  const fallbackStart = Math.round(overlay.startRatio * videoDuration * 10) / 10;
  const fallbackEnd = Math.round(overlay.endRatio * videoDuration * 10) / 10;

  if (!overlay.anchor) {
    return { startTime: fallbackStart, endTime: Math.max(fallbackEnd, fallbackStart + 0.3) };
  }

  const startWord = findWordByOccurrence(
    words,
    normalizeWord(overlay.anchor.startWord),
    overlay.anchor.startOccurrence
  );
  const endWord = findWordByOccurrence(
    words,
    normalizeWord(overlay.anchor.endWord),
    overlay.anchor.endOccurrence
  );
  if (!startWord || !endWord) {
    return { startTime: fallbackStart, endTime: Math.max(fallbackEnd, fallbackStart + 0.3) };
  }

  const startTime = Math.max(0, Math.round(startWord.start * 10) / 10);
  const endTime = Math.min(videoDuration, Math.round(endWord.end * 10) / 10);
  return { startTime, endTime: Math.max(endTime, startTime + 0.3) };
}

export default function VideoEditor() {
  const [step, setStep] = useState<EditorStep>("upload");
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);

  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  const [captionStyle, setCaptionStyle] = useState("karaoke");
  const [captionCustom, setCaptionCustom] = useState<CaptionCustomStyle>(DEFAULT_CAPTION_CUSTOM);
  const [showTextStylePanel, setShowTextStylePanel] = useState(false);
  const [scenePlan, setScenePlan] = useState<ScenePlan | null>(null);
  const [showPreviewSubtitles, setShowPreviewSubtitles] = useState(true);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const [selectedSubtitleIndex, setSelectedSubtitleIndex] = useState<number | null>(null);
  const [selectedOverlayIndex, setSelectedOverlayIndex] = useState<number | null>(null);
  const [videoSize, setVideoSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const [overlayFeedback, setOverlayFeedback] = useState("");
  const [clickedOverlayIndex, setClickedOverlayIndex] = useState<number | null>(null);
  const [isModifyingOverlay, setIsModifyingOverlay] = useState(false);

  // Custom code overlay generation
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [customDuration, setCustomDuration] = useState(5);
  const [isGeneratingCustom, setIsGeneratingCustom] = useState(false);

  // SFX
  const [sfxFiles, setSfxFiles] = useState<SfxFile[]>([]);
  const [showSfxPanel, setShowSfxPanel] = useState(false);
  const [isUploadingSfx, setIsUploadingSfx] = useState(false);
  const sfxInputRef = useRef<HTMLInputElement>(null);

  // Template picker
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [mgTemplates, setMgTemplates] = useState<MgStyleTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);

  // Scene plan templates (save/load scene as schema)
  const [showSaveSchemaDialog, setShowSaveSchemaDialog] = useState(false);
  const [schemaName, setSchemaName] = useState("");
  const [schemaDesc, setSchemaDesc] = useState("");
  const [isSavingSchema, setIsSavingSchema] = useState(false);
  const [showSchemaLoader, setShowSchemaLoader] = useState(false);
  const [scenePlanTemplates, setScenePlanTemplates] = useState<MgScenePlanTemplate[]>([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const transcriptionRef = useRef<TranscriptionData | null>(null);
  const didAutoTranscribe = useRef(false);

  // Video source URL (source or rendered output)
  const videoSrc = useMemo(() => {
    const token = getAuthToken();
    const suffix = token ? `?token=${token}` : "";
    if (outputUrl) return outputUrl.replace("/download/", "/preview/") + suffix;
    if (fileId) return `/api/video-editor/source/${fileId}${suffix}`;
    return null;
  }, [fileId, outputUrl]);

  // Track video element size for overlay positioning
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setVideoSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [step, videoSrc]);

  // Auto-transcribe on entering editor
  useEffect(() => {
    if (step !== "editor" || !fileId || didAutoTranscribe.current) return;
    didAutoTranscribe.current = true;
    transcribe();
  }, [step, fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Callbacks ---

  const handleUploaded = useCallback((id: string, name: string) => {
    // Always start a fresh editing session for a newly uploaded file.
    setSubtitles([]);
    setScenePlan(null);
    setOutputUrl(null);
    setCurrentTime(0);
    setVideoDuration(0);
    setIsPlaying(false);
    setSelectedSubtitleIndex(null);
    setSelectedOverlayIndex(null);
    setClickedOverlayIndex(null);
    setOverlayFeedback("");
    setShowTemplatePicker(false);
    setShowSchemaLoader(false);
    setShowCustomPanel(false);
    setShowPreviewSubtitles(true);
    transcriptionRef.current = null;

    setFileId(id);
    setFileName(name);
    setStep("editor");
    setError(null);
    setSfxFiles([]);
    didAutoTranscribe.current = false;
  }, []);

  const transcribe = useCallback(async () => {
    if (!fileId) return;
    setIsTranscribing(true);
    setError(null);
    try {
      const result = await apiRequest("POST", `/api/video-editor/transcribe/${fileId}`);
      const rawWords: Array<{ word: string; start: number; end: number }> = result.words ?? [];

      transcriptionRef.current = {
        fullText: result.fullText ?? "",
        words: rawWords,
        duration: result.duration ?? 30,
        language: result.language ?? "pl",
      };

      const autoSubs: Subtitle[] = (result.segments ?? []).map(
        (seg: { start: number; end: number; text: string }) => ({
          startTime: seg.start,
          endTime: seg.end,
          text: seg.text,
          words: rawWords.filter((w) => w.start >= seg.start && w.start < seg.end),
        })
      );
      setSubtitles(autoSubs);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Blad transkrypcji");
    } finally {
      setIsTranscribing(false);
    }
  }, [fileId]);

  const generateMotionPlan = useCallback(async () => {
    if (!fileId) return;
    setIsGeneratingPlan(true);
    setError(null);
    try {
      // Ensure transcription exists
      if (!transcriptionRef.current) {
        await transcribe();
      }
      if (!transcriptionRef.current) {
        setError("Brak transkrypcji — nie mozna wygenerowac planu");
        return;
      }
      const result = await apiRequest(
        "POST",
        `/api/video-editor/generate-scene-plan/${fileId}`,
        transcriptionRef.current
      );
      setScenePlan(result.scenePlan);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Blad generowania planu AI");
    } finally {
      setIsGeneratingPlan(false);
    }
  }, [fileId, transcribe]);

  const handleRender = useCallback(async () => {
    if (!fileId) return;
    setIsProcessing(true);
    setError(null);
    try {
      const hasMG = scenePlan && scenePlan.overlays.length > 0;
      const endpoint = hasMG
        ? `/api/video-editor/process-motion-graphics/${fileId}`
        : `/api/video-editor/process/${fileId}`;
      const activeSfx = sfxFiles.filter((f) => f.startTime >= 0);
      const sfxPayload = activeSfx.map((f) => ({
        id: f.fileName,
        fileName: f.fileName,
        startTime: f.startTime,
        duration: f.duration,
        volume: f.volume,
      }));
      const body = hasMG
        ? {
            subtitles: subtitles.filter((s) => s.text.trim()),
            scenePlan,
            style: captionStyle,
            customStyle: captionCustom,
            outputFormat: "mp4",
            soundEffects: sfxPayload,
          }
        : {
            subtitles: subtitles.filter((s) => s.text.trim()),
            style: captionStyle,
            customStyle: captionCustom,
            outputFormat: "mp4",
            soundEffects: sfxPayload,
          };

      const result = await apiRequest("POST", endpoint, body);
      setOutputUrl(result.outputPath);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Blad renderowania");
    } finally {
      setIsProcessing(false);
    }
  }, [fileId, subtitles, captionStyle, captionCustom, scenePlan]);

  const handleDownload = useCallback(() => {
    if (!outputUrl) return;
    const token = getAuthToken();
    const link = document.createElement("a");
    link.href = `${outputUrl}${token ? `?token=${token}` : ""}`;
    link.download = `montaz_${fileName}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [outputUrl, fileName]);

  const resetEditor = useCallback(() => {
    if (fileId) apiRequest("DELETE", `/api/video-editor/file/${fileId}`).catch(() => {});
    setStep("upload");
    setFileId(null);
    setFileName("");
    setSubtitles([]);
    setCaptionStyle("karaoke");
    setScenePlan(null);
    setOutputUrl(null);
    setError(null);
    setCurrentTime(0);
    setVideoDuration(0);
    transcriptionRef.current = null;
    didAutoTranscribe.current = false;
  }, [fileId]);

  // SFX loading & upload
  const loadSfxFiles = useCallback(async () => {
    if (!fileId) return;
    try {
      const data = await apiRequest("GET", `/api/video-editor/sfx/${fileId}`);
      setSfxFiles(
        (data.files ?? []).map((f: { name: string; size: number; modified: string }) => ({
          id: f.name,
          fileName: f.name,
          startTime: 0,
          duration: 0,
          volume: 1,
          path: `/api/video-editor/sfx/${fileId}/${f.name}`,
        }))
      );
    } catch {
      /* ignore */
    }
  }, [fileId]);

  const uploadSfxFile = useCallback(
    async (file: File) => {
      if (!fileId) return;
      setIsUploadingSfx(true);
      try {
        const formData = new FormData();
        formData.append("sfx", file);
        const token = (await import("@/lib/queryClient")).getAuthToken();
        const resp = await fetch(`/api/video-editor/sfx/${fileId}`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!resp.ok) throw new Error("Upload failed");
        const data = await resp.json();
        setSfxFiles((prev) => [
          ...prev,
          {
            id: data.id,
            fileName: data.fileName,
            startTime: currentTime,
            duration: 0,
            volume: 1,
            path: data.path,
          },
        ]);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Błąd uploadu SFX");
      } finally {
        setIsUploadingSfx(false);
      }
    },
    [fileId, currentTime]
  );

  const deleteSfxFile = useCallback(
    async (sfxName: string) => {
      if (!fileId) return;
      try {
        await apiRequest("DELETE", `/api/video-editor/sfx/${fileId}/${sfxName}`);
        setSfxFiles((prev) => prev.filter((f) => f.fileName !== sfxName));
      } catch {
        /* ignore */
      }
    },
    [fileId]
  );

  const updateSfxField = useCallback(
    <K extends keyof SfxFile>(index: number, key: K, value: SfxFile[K]) => {
      setSfxFiles((prev) => prev.map((f, i) => (i === index ? { ...f, [key]: value } : f)));
    },
    []
  );

  // Template loading
  const loadMgTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const data = await apiRequest("GET", "/api/video-editor/style-templates");
      setMgTemplates(data);
    } catch {
      /* ignore */
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  const buildTemplateAdaptPrompt = useCallback((sampleText: string, forceTwoLine: boolean) => {
    if (forceTwoLine) {
      return `Refactor this Remotion component without changing the visual identity.

STRICT rules:
1. Keep the same letter-by-letter style and typography feeling.
2. Replace title/subtitle text constants with EXACT tokens:
   - TITLE line: ${DYNAMIC_LINE_1_TOKEN}
   - SUBTITLE line: ${DYNAMIC_LINE_2_TOKEN}
3. First line must stay white, second line must stay red accent.
4. Move the block to bottom subtitle-safe area (not center): use justifyContent: "flex-end" and bottom padding.
5. Speed up animation so both lines fully appear quickly: letter delay should be 1 frame and subtitle start around 12 frames.
6. Keep background transparent (no black fullscreen background).
7. Keep export valid for Remotion runtime.

Example text split source: "${sampleText}"`;
    }

    return `Refactor this Remotion component without changing visual style.

Rules:
1. Keep the same animation choreography, colors, fonts, timing feel and layout.
2. Replace all visible hardcoded headline/body words with EXACT literal token: ${DYNAMIC_TEXT_TOKEN}
3. Keep this token as plain string text in JSX/styles where text is shown.
4. Do NOT add black full-screen background. If there is a black AbsoluteFill background, make it transparent.
5. Keep component export valid for Remotion runtime.

Example text to preserve rhythm for: "${sampleText}"`;
  }, []);

  const adaptTemplateOverlaysToDynamicText = useCallback(
    async (template: MgStyleTemplate): Promise<MgStyleTemplate["overlays"]> => {
      if (subtitles.length === 0 || template.overlays.length === 0) {
        return template.overlays;
      }

      const sampleText = subtitles
        .map((subtitle) => subtitle.text.trim())
        .filter((text) => text.length > 0)
        .slice(0, 3)
        .join(" ")
        .slice(0, 220);

      if (!sampleText) return template.overlays;

      const prompt = buildTemplateAdaptPrompt(sampleText, false);
      const adaptedOverlays = await Promise.all(
        template.overlays.map(async (overlayTemplate) => {
          if (overlayTemplate.type !== "custom-code") {
            return overlayTemplate;
          }

          try {
            const forceTwoLine = looksLikeTitleSubtitleTemplate(overlayTemplate.code);
            const finalPrompt = forceTwoLine ? buildTemplateAdaptPrompt(sampleText, true) : prompt;

            const result = await apiRequest("POST", "/api/video-editor/follow-up-edit", {
              currentCode: overlayTemplate.code,
              editPrompt: finalPrompt,
            });

            const needsTwoLineTokens =
              forceTwoLine &&
              (!String(result.code ?? "").includes(DYNAMIC_LINE_1_TOKEN) ||
                !String(result.code ?? "").includes(DYNAMIC_LINE_2_TOKEN));
            const needsSingleToken =
              !forceTwoLine && !String(result.code ?? "").includes(DYNAMIC_TEXT_TOKEN);

            if (needsTwoLineTokens || needsSingleToken) {
              const strictPrompt = forceTwoLine
                ? `Rewrite this component so title/subtitle use EXACT tokens ${DYNAMIC_LINE_1_TOKEN} and ${DYNAMIC_LINE_2_TOKEN}. Keep white first line, red second line, bottom position and fast animation.`
                : `Rewrite this component so EVERY visible text label uses EXACT token ${DYNAMIC_TEXT_TOKEN}. Do not keep original words.`;

              const strictResult = await apiRequest("POST", "/api/video-editor/follow-up-edit", {
                currentCode: result.code,
                editPrompt: strictPrompt,
              });

              return {
                ...overlayTemplate,
                code: strictResult.code,
                compiled: strictResult.compiled,
                name: strictResult.name ?? overlayTemplate.name,
              };
            }

            return {
              ...overlayTemplate,
              code: result.code,
              compiled: result.compiled,
              name: result.name ?? overlayTemplate.name,
            };
          } catch {
            return overlayTemplate;
          }
        })
      );

      return adaptedOverlays;
    },
    [buildTemplateAdaptPrompt, subtitles]
  );

  const applyTemplate = useCallback(
    async (template: MgStyleTemplate) => {
      setIsApplyingTemplate(true);
      setCaptionCustom({
        bgColor: template.captionStyle.bgColor,
        textColor: template.captionStyle.textColor,
        fontFamily: template.captionStyle.fontFamily ?? "Arial, Helvetica, sans-serif",
        fontSize: template.captionStyle.fontSize ?? "1.15em",
        fontWeight: template.captionStyle.fontWeight ?? 900,
        textTransform: template.captionStyle.textTransform ?? "none",
        dropShadow: template.captionStyle.dropShadow,
        shadowColor: template.captionStyle.shadowColor,
        shadowBlur: template.captionStyle.shadowBlur,
        entranceEffect: template.captionStyle.entranceEffect,
      });

      try {
        const resolvedTemplateOverlays = await adaptTemplateOverlaysToDynamicText(template);

        if (resolvedTemplateOverlays.length > 0) {
          const templateOverlays = resolvedTemplateOverlays.flatMap((overlayTemplate) => {
            const overlayDuration = Math.max(0.5, overlayTemplate.durationFrames / 30);
            const customPayload = sanitizeCustomCodePayload({
              code: overlayTemplate.code,
              compiled: overlayTemplate.compiled,
              name: overlayTemplate.name,
            });

            const firstSubtitleText =
              subtitles.find((subtitle) => subtitle.text.trim().length > 0)?.text ?? "";
            const forceTwoLine = looksLikeTitleSubtitleTemplate(String(customPayload.code ?? ""));
            const singleCode =
              typeof customPayload.code === "string"
                ? forceTwoLine
                  ? replaceTwoLineTokens(customPayload.code, firstSubtitleText)
                  : replaceDynamicTextToken(customPayload.code, firstSubtitleText)
                : customPayload.code;
            const singleCompiled =
              typeof customPayload.compiled === "string"
                ? forceTwoLine
                  ? replaceTwoLineTokens(customPayload.compiled, firstSubtitleText)
                  : replaceDynamicTextToken(customPayload.compiled, firstSubtitleText)
                : customPayload.compiled;

            const payloadForSingleUse = {
              ...customPayload,
              code: singleCode,
              compiled: singleCompiled,
              dynamicText: firstSubtitleText,
              data: { dynamicText: firstSubtitleText },
            };

            if (overlayTemplate.triggerMode !== "per-subtitle" || subtitles.length === 0) {
              return [
                {
                  type: "custom-code" as const,
                  startTime: currentTime,
                  endTime: currentTime + overlayDuration,
                  payload: payloadForSingleUse,
                },
              ];
            }

            return subtitles
              .filter((subtitle) => subtitle.text.trim().length > 0)
              .map((subtitle) => ({
                type: "custom-code" as const,
                startTime: subtitle.startTime,
                endTime: Math.max(subtitle.endTime, subtitle.startTime + 0.3),
                payload: {
                  ...customPayload,
                  code: (() => {
                    if (typeof customPayload.code !== "string") return customPayload.code;
                    return forceTwoLine
                      ? replaceTwoLineTokens(customPayload.code, subtitle.text)
                      : replaceDynamicTextToken(customPayload.code, subtitle.text);
                  })(),
                  compiled: (() => {
                    if (typeof customPayload.compiled !== "string") return customPayload.compiled;
                    return forceTwoLine
                      ? replaceTwoLineTokens(customPayload.compiled, subtitle.text)
                      : replaceDynamicTextToken(customPayload.compiled, subtitle.text);
                  })(),
                  dynamicText: subtitle.text,
                  data: { dynamicText: subtitle.text },
                },
              }));
          });

          const existingPlan: ScenePlan = scenePlan ?? { captionStyle, overlays: [] };
          setScenePlan({
            ...existingPlan,
            overlays: [...existingPlan.overlays, ...templateOverlays],
          });
        }

        setShowTemplatePicker(false);
      } finally {
        setIsApplyingTemplate(false);
      }
    },
    [adaptTemplateOverlaysToDynamicText, scenePlan, currentTime, subtitles, captionStyle]
  );

  // Scene plan templates (save/load schemat)
  const handleSaveSchema = useCallback(async () => {
    if (!scenePlan || !schemaName.trim() || !videoDuration) return;
    setIsSavingSchema(true);
    try {
      const words = transcriptionRef.current?.words ?? [];
      const normalizedOverlays = scenePlan.overlays.map((o) => ({
        ...(words.length > 0
          ? (() => {
              const startIndex = findClosestWordIndex(words, o.startTime);
              const endIndex = findClosestWordIndex(words, o.endTime);
              const startWord = words[startIndex];
              const endWord = words[endIndex];

              if (!startWord || !endWord) return {};

              const normalizedStartWord = normalizeWord(startWord.word);
              const normalizedEndWord = normalizeWord(endWord.word);
              if (!normalizedStartWord || !normalizedEndWord) return {};

              return {
                anchor: {
                  startWord: normalizedStartWord,
                  startOccurrence: countWordOccurrence(words, normalizedStartWord, startIndex),
                  endWord: normalizedEndWord,
                  endOccurrence: countWordOccurrence(words, normalizedEndWord, endIndex),
                },
              };
            })()
          : {}),
        type: o.type,
        startRatio: o.startTime / videoDuration,
        endRatio: o.endTime / videoDuration,
        payload: o.payload,
      }));
      await apiRequest("POST", "/api/video-editor/scene-plan-templates", {
        name: schemaName.trim(),
        description: schemaDesc.trim() || null,
        captionStyle: scenePlan.captionStyle,
        overlays: normalizedOverlays,
      });
      setShowSaveSchemaDialog(false);
      setSchemaName("");
      setSchemaDesc("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Błąd zapisu schematu");
    } finally {
      setIsSavingSchema(false);
    }
  }, [scenePlan, schemaName, schemaDesc, videoDuration]);

  const loadScenePlanTemplates = useCallback(async () => {
    setIsLoadingSchemas(true);
    try {
      const data = await apiRequest("GET", "/api/video-editor/scene-plan-templates");
      setScenePlanTemplates(data);
    } catch {
      /* ignore */
    } finally {
      setIsLoadingSchemas(false);
    }
  }, []);

  const applyScenePlanTemplate = useCallback(
    (template: MgScenePlanTemplate) => {
      if (!videoDuration) return;
      const words = transcriptionRef.current?.words ?? [];
      const typedTemplateOverlays = template.overlays as ScenePlanTemplateOverlayWithAnchor[];

      const scaledOverlays = typedTemplateOverlays
        .map((overlayTemplate) => {
          const resolvedTiming = resolveOverlayTimesFromAnchor(
            overlayTemplate,
            words,
            videoDuration
          );

          const payload =
            overlayTemplate.type === "custom-code"
              ? sanitizeCustomCodePayload(overlayTemplate.payload)
              : overlayTemplate.payload;

          return {
            type: overlayTemplate.type,
            startTime: resolvedTiming.startTime,
            endTime: resolvedTiming.endTime,
            payload,
          };
        })
        .map((o) => ({
          type: o.type,
          startTime: o.startTime,
          endTime: o.endTime,
          payload: o.payload,
        }));

      setScenePlan({
        captionStyle: template.captionStyle,
        overlays: scaledOverlays,
      });
      setCaptionStyle(template.captionStyle);
      setShowSchemaLoader(false);
    },
    [videoDuration]
  );

  const deleteScenePlanTemplate = useCallback(async (id: string) => {
    try {
      await apiRequest("DELETE", `/api/video-editor/scene-plan-templates/${id}`);
      setScenePlanTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch {
      /* ignore */
    }
  }, []);

  // Video player sync
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const duration = videoRef.current?.duration;
    if (typeof duration === "number" && Number.isFinite(duration) && duration > 0) {
      setVideoDuration(duration);
      return;
    }

    setVideoDuration(0);
  }, []);

  const handleSeek = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, []);

  // --- Timeline selection & timing ---

  const handleSubtitleTimingChange = useCallback(
    (index: number, startTime: number, endTime: number) => {
      setSubtitles((prev) =>
        prev.map((sub, i) => (i === index ? { ...sub, startTime, endTime } : sub))
      );
    },
    []
  );

  const handleOverlayTimingChange = useCallback(
    (index: number, startTime: number, endTime: number) => {
      if (!scenePlan) return;
      setScenePlan((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          overlays: prev.overlays.map((o, i) => (i === index ? { ...o, startTime, endTime } : o)),
        };
      });
    },
    [scenePlan]
  );

  const handleSubtitleSelect = useCallback(
    (index: number) => {
      setSelectedSubtitleIndex(index);
      setSelectedOverlayIndex(null);
      const sub = subtitles[index];
      if (sub && videoRef.current) {
        videoRef.current.currentTime = sub.startTime;
        setCurrentTime(sub.startTime);
      }
    },
    [subtitles]
  );

  const handleOverlaySelect = useCallback(
    (index: number) => {
      setSelectedOverlayIndex(index);
      setSelectedSubtitleIndex(null);
      const overlay = scenePlan?.overlays[index];
      if (overlay && videoRef.current) {
        videoRef.current.currentTime = overlay.startTime;
        setCurrentTime(overlay.startTime);
      }
    },
    [scenePlan]
  );

  // --- Delete from timeline ---

  const handleOverlayDelete = useCallback(
    (index: number) => {
      if (!scenePlan) return;
      setScenePlan((prev) => {
        if (!prev) return prev;
        return { ...prev, overlays: prev.overlays.filter((_, i) => i !== index) };
      });
      setSelectedOverlayIndex(null);
    },
    [scenePlan]
  );

  const handleCustomOverlayOffsetChange = useCallback((index: number, nextOffsetY: number) => {
    setScenePlan((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        overlays: prev.overlays.map((overlay, overlayIndex) => {
          if (overlayIndex !== index || overlay.type !== "custom-code") return overlay;
          return {
            ...overlay,
            payload: {
              ...overlay.payload,
              previewOffsetY: nextOffsetY,
            },
          };
        }),
      };
    });
  }, []);

  // --- Split at playhead ---

  const handleSubtitleSplit = useCallback((index: number, splitTime: number) => {
    setSubtitles((prev) => {
      const sub = prev[index];
      if (!sub || splitTime <= sub.startTime || splitTime >= sub.endTime) return prev;
      const left: Subtitle = { ...sub, endTime: splitTime };
      const right: Subtitle = { ...sub, startTime: splitTime };
      return [...prev.slice(0, index), left, right, ...prev.slice(index + 1)];
    });
    setSelectedSubtitleIndex(null);
  }, []);

  const handleOverlaySplit = useCallback(
    (index: number, splitTime: number) => {
      if (!scenePlan) return;
      setScenePlan((prev) => {
        if (!prev) return prev;
        const overlay = prev.overlays[index];
        if (!overlay || splitTime <= overlay.startTime || splitTime >= overlay.endTime) return prev;
        const left = { ...overlay, endTime: splitTime };
        const right = { ...overlay, startTime: splitTime };
        return {
          ...prev,
          overlays: [
            ...prev.overlays.slice(0, index),
            left,
            right,
            ...prev.overlays.slice(index + 1),
          ],
        };
      });
      setSelectedOverlayIndex(null);
    },
    [scenePlan]
  );

  // --- Subtitle editing ---

  // --- Overlay click-to-edit ---

  const handleOverlayPreviewClick = useCallback(
    (index: number) => {
      setClickedOverlayIndex(index);
      setOverlayFeedback("");
      setSelectedOverlayIndex(index);
      setSelectedSubtitleIndex(null);
      const overlay = scenePlan?.overlays[index];
      if (overlay && videoRef.current) {
        videoRef.current.currentTime = overlay.startTime;
        setCurrentTime(overlay.startTime);
      }
    },
    [scenePlan]
  );

  const handleOverlayModify = useCallback(async () => {
    if (clickedOverlayIndex === null || !scenePlan || !overlayFeedback.trim()) return;
    const overlay = scenePlan.overlays[clickedOverlayIndex];
    if (!overlay) return;

    setIsModifyingOverlay(true);
    try {
      const transcriptContext = transcriptionRef.current
        ? transcriptionRef.current.words
            .filter((w) => w.start >= overlay.startTime - 2 && w.start <= overlay.endTime + 2)
            .map((w) => w.word)
            .join(" ")
        : "";

      const result = await apiRequest("POST", "/api/video-editor/modify-overlay", {
        overlay,
        feedback: overlayFeedback,
        transcriptContext,
      });

      setScenePlan((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          overlays: prev.overlays.map((o, i) =>
            i === clickedOverlayIndex
              ? { ...result.overlay, startTime: o.startTime, endTime: o.endTime }
              : o
          ),
        };
      });
      setClickedOverlayIndex(null);
      setOverlayFeedback("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Błąd modyfikacji overlay");
    } finally {
      setIsModifyingOverlay(false);
    }
  }, [clickedOverlayIndex, scenePlan, overlayFeedback]);

  const handleGenerateCustomOverlay = useCallback(async () => {
    if (!customPrompt.trim() || !scenePlan) return;
    setIsGeneratingCustom(true);
    try {
      const startTime = currentTime;
      const endTime = Math.min(currentTime + customDuration, videoDuration - 0.5);

      const transcriptContext = transcriptionRef.current
        ? transcriptionRef.current.words
            .filter((w) => w.start >= startTime - 1 && w.start <= endTime + 1)
            .map((w) => w.word)
            .join(" ")
        : "";

      const result = await apiRequest("POST", "/api/video-editor/generate-custom-overlay", {
        prompt: customPrompt,
        startTime,
        endTime,
        language: "pl",
        transcriptContext,
      });

      setScenePlan((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          overlays: [...prev.overlays, result.overlay].sort(
            (a: { startTime: number }, b: { startTime: number }) => a.startTime - b.startTime
          ),
        };
      });
      setShowCustomPanel(false);
      setCustomPrompt("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Błąd generowania custom overlay");
    } finally {
      setIsGeneratingCustom(false);
    }
  }, [customPrompt, customDuration, currentTime, videoDuration, scenePlan]);

  const handleSubtitleTextChange = useCallback((index: number, text: string) => {
    setSubtitles((prev) =>
      prev.map((sub, i) => (i === index ? { ...sub, text, words: undefined } : sub))
    );
  }, []);

  const handleSubtitleDelete = useCallback((index: number) => {
    setSubtitles((prev) => prev.filter((_, i) => i !== index));
    setSelectedSubtitleIndex(null);
  }, []);

  const handleSubtitleAdd = useCallback(() => {
    const lastEnd = subtitles.length > 0 ? subtitles[subtitles.length - 1].endTime : 0;
    const newSub: Subtitle = {
      startTime: lastEnd,
      endTime: Math.min(lastEnd + 3, videoDuration || 30),
      text: "",
    };
    setSubtitles((prev) => [...prev, newSub]);
    setSelectedSubtitleIndex(subtitles.length);
  }, [subtitles, videoDuration]);

  const selectedSubtitle = selectedSubtitleIndex !== null ? subtitles[selectedSubtitleIndex] : null;

  // --- Status line ---
  const isAnyLoading = isTranscribing || isGeneratingPlan || isProcessing;

  const rotatingMsg = useRotatingMessage(undefined, isAnyLoading);

  const statusText = useMemo(() => {
    if (isTranscribing) return "Transkrybuje audio (Whisper)...";
    if (isGeneratingPlan) return "AI analizuje i planuje motion graphics...";
    if (isProcessing) return "Renderuje wideo (Remotion)...";
    return null;
  }, [isTranscribing, isGeneratingPlan, isProcessing]);

  // --- Render ---

  if (step === "upload") {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6">
        <VideoUpload
          onUploaded={handleUploaded}
          isUploading={isUploading}
          setIsUploading={setIsUploading}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top bar */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <Film size={14} className="text-white/40" />
          <span className="text-sm text-white/60 truncate max-w-[200px]">{fileName}</span>
          {outputUrl && (
            <span className="px-1.5 py-0.5 text-[9px] bg-green-500/20 text-green-400 rounded">
              GOTOWE
            </span>
          )}
        </div>
        <button
          onClick={resetEditor}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/40 hover:text-white/70 transition"
        >
          <Trash2 size={11} />
          Nowy
        </button>
      </div>

      {/* Video preview */}
      <div className="flex-1 min-h-0 flex items-center justify-center bg-black/30 relative p-2">
        {videoSrc ? (
          <div className="relative h-full w-full flex items-center justify-center">
            <video
              key={videoSrc}
              ref={videoRef}
              src={videoSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              className="max-h-full max-w-full rounded-lg object-contain"
              playsInline
              preload="metadata"
              onClick={togglePlay}
              style={{ cursor: "pointer" }}
            />
            {/* Overlay positioned exactly over the video — only for source (not rendered output) */}
            {!outputUrl && videoSize.width > 0 && videoSize.height > 0 && (
              <div
                className="absolute overflow-hidden rounded-lg"
                style={{
                  width: videoSize.width,
                  height: videoSize.height,
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: scenePlan ? "auto" : "none",
                }}
              >
                <VideoEditorSectionBoundary label="Preview Overlay">
                  <VideoPreviewOverlay
                    currentTime={currentTime}
                    subtitles={subtitles}
                    overlays={scenePlan?.overlays ?? []}
                    captionStyle={captionStyle}
                    captionCustom={captionCustom}
                    onOverlayClick={scenePlan ? handleOverlayPreviewClick : undefined}
                    onCustomOverlayOffsetChange={
                      scenePlan ? handleCustomOverlayOffsetChange : undefined
                    }
                    showSubtitles={showPreviewSubtitles}
                  />
                </VideoEditorSectionBoundary>
              </div>
            )}
          </div>
        ) : (
          <div className="text-white/20 text-sm">Ladowanie podgladu...</div>
        )}
      </div>

      {/* Toolbar */}
      <div className="shrink-0 flex items-center gap-1.5 px-3 py-2 border-y border-white/[0.06] bg-white/[0.02] flex-wrap">
        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          className="p-1.5 rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-white transition"
          title={isPlaying ? "Pauza" : "Odtwórz"}
        >
          {isPlaying ? <Pause size={14} /> : <Play size={14} className="translate-x-[8%]" />}
        </button>

        <div className="w-px h-5 bg-white/[0.08] mx-1" />

        {/* Auto-transcribe */}
        <button
          onClick={transcribe}
          disabled={isTranscribing}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 transition disabled:opacity-50"
          title="Auto napisy (Whisper AI)"
        >
          {isTranscribing ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          Napisy AI
        </button>

        {/* Caption style */}
        <select
          value={captionStyle}
          onChange={(e) => setCaptionStyle(e.target.value)}
          className="px-2 py-1.5 text-[11px] bg-black/40 border border-white/10 rounded-lg text-white/70"
        >
          {CAPTION_STYLES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => setShowPreviewSubtitles((prev) => !prev)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg transition ${
            showPreviewSubtitles
              ? "bg-white/[0.08] text-white/70 hover:bg-white/[0.12]"
              : "bg-white/[0.04] text-white/35 hover:bg-white/[0.08]"
          }`}
          title="Pokaż lub ukryj pasek napisów w podglądzie"
        >
          {showPreviewSubtitles ? <Eye size={11} /> : <EyeOff size={11} />}
          Napisy
        </button>

        {/* Text style customization */}
        <button
          onClick={() => setShowTextStylePanel((prev) => !prev)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg transition ${
            showTextStylePanel
              ? "bg-cyan-500/25 text-cyan-300"
              : "bg-white/[0.06] hover:bg-white/10 text-white/50 hover:text-white/70"
          }`}
          title="Dostosuj styl tekstu napisów"
        >
          <Palette size={11} />
          Styl
        </button>

        {/* Load MG style template */}
        <button
          onClick={() => {
            setShowTemplatePicker(!showTemplatePicker);
            if (!showTemplatePicker) loadMgTemplates();
          }}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg transition ${
            showTemplatePicker
              ? "bg-violet-500/25 text-violet-300"
              : "bg-white/[0.06] hover:bg-white/10 text-white/50 hover:text-white/70"
          }`}
          title="Załaduj szablon stylu z Motion Graphics"
        >
          <FolderOpen size={11} />
          Szablony
        </button>

        {/* Add subtitle manually */}
        <button
          onClick={handleSubtitleAdd}
          className="flex items-center gap-1 px-2 py-1.5 text-[11px] rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/40 hover:text-white/70 transition"
          title="Dodaj napis ręcznie"
        >
          <Plus size={10} />
        </button>

        <div className="w-px h-5 bg-white/[0.08] mx-1" />

        {/* AI Motion Graphics */}
        <button
          onClick={generateMotionPlan}
          disabled={isGeneratingPlan}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg bg-violet-500/15 hover:bg-violet-500/25 text-violet-300 transition disabled:opacity-50"
          title="Generuj AI motion graphics (wykresy, karty, porownania)"
        >
          {isGeneratingPlan ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
          AI Motion
        </button>

        {scenePlan && (
          <button
            onClick={() => setScenePlan(null)}
            className="p-1 rounded text-white/20 hover:text-red-400 transition"
            title="Usun plan MG"
          >
            <Trash2 size={10} />
          </button>
        )}

        {/* Save scene plan as template */}
        {scenePlan && scenePlan.overlays.length > 0 && (
          <button
            onClick={() => setShowSaveSchemaDialog(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 transition"
            title="Zapisz obecny plan MG jako schemat do ponownego użycia"
          >
            <Save size={11} />
            Zapisz schemat
          </button>
        )}

        {/* Load scene plan template */}
        <button
          onClick={() => {
            setShowSchemaLoader(!showSchemaLoader);
            if (!showSchemaLoader) loadScenePlanTemplates();
          }}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg transition ${
            showSchemaLoader
              ? "bg-teal-500/25 text-teal-300"
              : "bg-white/[0.06] hover:bg-white/10 text-white/50 hover:text-white/70"
          }`}
          title="Załaduj schemat motion graphics z zapisanych"
        >
          <BookOpen size={11} />
          Schematy
        </button>

        {/* Custom Code Overlay */}
        {scenePlan && (
          <button
            onClick={() => setShowCustomPanel((prev) => !prev)}
            disabled={isGeneratingCustom}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 transition disabled:opacity-50"
            title="Dodaj custom overlay generowany przez AI (dowolna animacja)"
          >
            {isGeneratingCustom ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Sparkles size={11} />
            )}
            Custom
          </button>
        )}

        {/* SFX */}
        <button
          onClick={() => {
            setShowSfxPanel((prev) => !prev);
            if (!showSfxPanel && sfxFiles.length === 0) loadSfxFiles();
          }}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-lg transition ${
            showSfxPanel
              ? "bg-green-500/25 text-green-300"
              : "bg-white/[0.06] hover:bg-white/10 text-white/50 hover:text-white/70"
          }`}
          title="Efekty dźwiękowe (SFX)"
        >
          <Music size={11} />
          SFX
          {sfxFiles.length > 0 && (
            <span className="ml-0.5 px-1 py-0 text-[9px] bg-green-500/30 text-green-300 rounded-full">
              {sfxFiles.length}
            </span>
          )}
        </button>

        <div className="flex-1" />

        {/* Render */}
        <button
          onClick={handleRender}
          disabled={isAnyLoading || subtitles.length === 0}
          className={`flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-medium rounded-lg transition ${
            isAnyLoading || subtitles.length === 0
              ? "bg-white/[0.06] text-white/20 cursor-not-allowed"
              : "bg-[#ef3e29] hover:bg-[#ef3e29]/80 text-white"
          }`}
        >
          {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          Renderuj
        </button>

        {/* Download */}
        {outputUrl && (
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg bg-green-600 hover:bg-green-600/80 text-white transition"
          >
            <Download size={12} />
            Pobierz
          </button>
        )}
      </div>

      {/* Status / Error bar */}
      {(statusText || error) && (
        <div
          className={`shrink-0 px-4 py-2 text-xs flex items-center gap-2 ${
            error ? "bg-red-500/10 text-red-400" : "bg-purple-500/5 text-white/50"
          }`}
        >
          {statusText && !error && <Loader2 size={11} className="animate-spin text-purple-400" />}
          {error ? (
            error
          ) : (
            <div className="flex flex-col">
              <span>{statusText}</span>
              <span className="text-[10px] text-white/30 mt-0.5">{rotatingMsg}</span>
            </div>
          )}
        </div>
      )}

      {/* Subtitle edit panel — appears when a subtitle is selected */}
      {selectedSubtitle && selectedSubtitleIndex !== null && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 border-t border-white/[0.06] bg-white/[0.03]">
          <Pencil size={10} className="text-cyan-400/60 shrink-0" />
          <span className="text-[10px] text-white/30 shrink-0">#{selectedSubtitleIndex + 1}</span>
          <input
            type="text"
            value={selectedSubtitle.text}
            onChange={(e) => handleSubtitleTextChange(selectedSubtitleIndex, e.target.value)}
            className="flex-1 min-w-0 px-2 py-1 text-xs bg-black/40 border border-white/10 rounded-md text-white/90 placeholder-white/20 focus:border-cyan-500/50 focus:outline-none"
            placeholder="Tekst napisu..."
            autoFocus
          />
          <span className="text-[9px] text-white/20 font-mono shrink-0 tabular-nums">
            {selectedSubtitle.startTime.toFixed(1)}s – {selectedSubtitle.endTime.toFixed(1)}s
          </span>
          <button
            onClick={() => handleSubtitleDelete(selectedSubtitleIndex)}
            className="p-1 rounded text-white/20 hover:text-red-400 transition"
            title="Usuń napis"
          >
            <Trash2 size={10} />
          </button>
          <button
            onClick={() => setSelectedSubtitleIndex(null)}
            className="p-1 rounded text-white/20 hover:text-white/50 transition"
            title="Zamknij"
          >
            <X size={10} />
          </button>
        </div>
      )}

      {/* Custom overlay generation panel */}
      {showCustomPanel && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 border-t border-white/[0.06] bg-amber-500/[0.04]">
          <Sparkles size={10} className="text-amber-400/60 shrink-0" />
          <input
            type="text"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && customPrompt.trim()) handleGenerateCustomOverlay();
            }}
            className="flex-1 min-w-0 px-2 py-1 text-xs bg-black/40 border border-white/10 rounded-md text-white/90 placeholder-white/20 focus:border-amber-500/50 focus:outline-none"
            placeholder="Opisz animację: np. wykres słupkowy z danymi sprzedaży, animowany countdown..."
            disabled={isGeneratingCustom}
            autoFocus
          />
          <select
            value={customDuration}
            onChange={(e) => setCustomDuration(Number(e.target.value))}
            className="px-1.5 py-1 text-[10px] bg-black/40 border border-white/10 rounded-md text-white/60"
          >
            <option value={3}>3s</option>
            <option value={5}>5s</option>
            <option value={7}>7s</option>
            <option value={10}>10s</option>
          </select>
          <button
            onClick={handleGenerateCustomOverlay}
            disabled={isGeneratingCustom || !customPrompt.trim()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 transition disabled:opacity-40"
          >
            {isGeneratingCustom ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <Wand2 size={10} />
            )}
            Generuj
          </button>
          <button
            onClick={() => {
              setShowCustomPanel(false);
              setCustomPrompt("");
            }}
            className="p-1 rounded text-white/20 hover:text-white/50 transition"
          >
            <X size={10} />
          </button>
        </div>
      )}

      {/* Overlay feedback panel — appears when clicking an overlay in preview */}
      {clickedOverlayIndex !== null && scenePlan?.overlays[clickedOverlayIndex] && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 border-t border-white/[0.06] bg-violet-500/[0.04]">
          <MessageSquare size={10} className="text-violet-400/60 shrink-0" />
          <span className="text-[10px] text-white/30 shrink-0">
            {scenePlan.overlays[clickedOverlayIndex].type}
          </span>
          <input
            type="text"
            value={overlayFeedback}
            onChange={(e) => setOverlayFeedback(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && overlayFeedback.trim()) handleOverlayModify();
            }}
            className="flex-1 min-w-0 px-2 py-1 text-xs bg-black/40 border border-white/10 rounded-md text-white/90 placeholder-white/20 focus:border-violet-500/50 focus:outline-none"
            placeholder="Co zmienić? np. zmień gradient na czerwony, dodaj emoji..."
            disabled={isModifyingOverlay}
            autoFocus
          />
          <span className="text-[9px] text-white/30 shrink-0">
            Przeciągnij overlay, żeby podnieść/obniżyć
          </span>
          <button
            onClick={handleOverlayModify}
            disabled={isModifyingOverlay || !overlayFeedback.trim()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 transition disabled:opacity-40"
            title="Wyślij do AI"
          >
            {isModifyingOverlay ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <Send size={10} />
            )}
            Zmień
          </button>
          <button
            onClick={() => {
              setClickedOverlayIndex(null);
              setOverlayFeedback("");
            }}
            className="p-1 rounded text-white/20 hover:text-white/50 transition"
            title="Zamknij"
          >
            <X size={10} />
          </button>
        </div>
      )}

      {/* Template picker panel */}
      {showTemplatePicker && (
        <div className="shrink-0 border-t border-white/[0.06] bg-violet-500/[0.03] px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
              Szablony z Motion Graphics
            </span>
            <button
              onClick={() => setShowTemplatePicker(false)}
              className="p-1 rounded text-white/20 hover:text-white/50 transition"
            >
              <X size={10} />
            </button>
          </div>
          {isLoadingTemplates ? (
            <div className="flex items-center gap-2 py-2 text-xs text-white/30">
              <Loader2 size={11} className="animate-spin" />
              Ładowanie...
            </div>
          ) : mgTemplates.length === 0 ? (
            <p className="text-[10px] text-white/20 py-2">
              Brak szablonów. Stwórz je w Motion Graphics Studio.
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {mgTemplates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  disabled={isApplyingTemplate}
                  className="shrink-0 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:bg-violet-500/10 hover:border-violet-500/20 transition text-left min-w-[140px] disabled:opacity-40"
                >
                  <div className="text-[11px] text-white/70 font-medium truncate">{t.name}</div>
                  {t.description && (
                    <div className="text-[9px] text-white/30 truncate mt-0.5">{t.description}</div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[8px] bg-white/[0.06] text-white/20 px-1 py-0.5 rounded">
                      {t.category}
                    </span>
                    {t.overlays.length > 0 && (
                      <span className="text-[8px] text-violet-400/40">
                        {t.overlays.length} overlay
                      </span>
                    )}
                  </div>
                  {isApplyingTemplate && (
                    <div className="flex items-center gap-1 mt-1 text-[8px] text-violet-300/70">
                      <Loader2 size={9} className="animate-spin" />
                      AI dopasowuje tekst...
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save schema dialog */}
      {showSaveSchemaDialog && (
        <div className="shrink-0 border-t border-white/[0.06] bg-emerald-500/[0.03] px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
              Zapisz schemat MG
            </span>
            <button
              onClick={() => {
                setShowSaveSchemaDialog(false);
                setSchemaName("");
                setSchemaDesc("");
              }}
              className="p-1 rounded text-white/20 hover:text-white/50 transition"
            >
              <X size={10} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={schemaName}
              onChange={(e) => setSchemaName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && schemaName.trim()) handleSaveSchema();
              }}
              className="flex-1 min-w-0 px-2 py-1 text-xs bg-black/40 border border-white/10 rounded-md text-white/90 placeholder-white/20 focus:border-emerald-500/50 focus:outline-none"
              placeholder="Nazwa schematu..."
              autoFocus
            />
            <input
              type="text"
              value={schemaDesc}
              onChange={(e) => setSchemaDesc(e.target.value)}
              className="flex-1 min-w-0 px-2 py-1 text-xs bg-black/40 border border-white/10 rounded-md text-white/90 placeholder-white/20 focus:border-emerald-500/50 focus:outline-none"
              placeholder="Opis (opcjonalny)..."
            />
            <button
              onClick={handleSaveSchema}
              disabled={isSavingSchema || !schemaName.trim()}
              className="flex items-center gap-1 px-3 py-1 text-[10px] rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 transition disabled:opacity-40"
            >
              {isSavingSchema ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
              Zapisz
            </button>
          </div>
          <p className="text-[9px] text-white/20 mt-1">
            Schemat zapisze {scenePlan?.overlays.length ?? 0} overlay(ów) z proporcjonalnym
            timingiem — dopasuje się do długości nowego wideo.
          </p>
        </div>
      )}

      {/* Scene plan template loader */}
      {showSchemaLoader && (
        <div className="shrink-0 border-t border-white/[0.06] bg-teal-500/[0.03] px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
              Schematy MG
            </span>
            <button
              onClick={() => setShowSchemaLoader(false)}
              className="p-1 rounded text-white/20 hover:text-white/50 transition"
            >
              <X size={10} />
            </button>
          </div>
          {isLoadingSchemas ? (
            <div className="flex items-center gap-2 py-2 text-xs text-white/30">
              <Loader2 size={11} className="animate-spin" />
              Ładowanie...
            </div>
          ) : scenePlanTemplates.length === 0 ? (
            <p className="text-[10px] text-white/20 py-2">
              Brak zapisanych schematów. Wygeneruj plan MG i kliknij „Zapisz schemat".
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {scenePlanTemplates.map((t) => (
                <div key={t.id} className="shrink-0 relative group">
                  <button
                    onClick={() => applyScenePlanTemplate(t)}
                    disabled={!videoDuration}
                    className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:bg-teal-500/10 hover:border-teal-500/20 transition text-left min-w-[160px] disabled:opacity-30"
                  >
                    <div className="text-[11px] text-white/70 font-medium truncate">{t.name}</div>
                    {t.description && (
                      <div className="text-[9px] text-white/30 truncate mt-0.5">
                        {t.description}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[8px] bg-white/[0.06] text-white/20 px-1 py-0.5 rounded">
                        {t.captionStyle}
                      </span>
                      <span className="text-[8px] text-teal-400/40">
                        {t.overlays.length} overlay
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => deleteScenePlanTemplate(t.id)}
                    className="absolute -top-1 -right-1 hidden group-hover:flex w-4 h-4 items-center justify-center rounded-full bg-red-500/80 text-white transition"
                    title="Usuń schemat"
                  >
                    <X size={8} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SFX panel */}
      {showSfxPanel && (
        <div className="shrink-0 border-t border-white/[0.06] bg-green-500/[0.03] px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
              Efekty dźwiękowe (SFX)
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => sfxInputRef.current?.click()}
                disabled={isUploadingSfx || !fileId}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-md bg-green-500/15 hover:bg-green-500/25 text-green-300 transition disabled:opacity-40"
              >
                {isUploadingSfx ? (
                  <Loader2 size={9} className="animate-spin" />
                ) : (
                  <Upload size={9} />
                )}
                Wgraj SFX
              </button>
              <input
                ref={sfxInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    uploadSfxFile(f);
                    e.target.value = "";
                  }
                }}
              />
              <button
                onClick={() => setShowSfxPanel(false)}
                className="p-1 rounded text-white/20 hover:text-white/50 transition"
              >
                <X size={10} />
              </button>
            </div>
          </div>

          {sfxFiles.length === 0 ? (
            <p className="text-[10px] text-white/20 py-1">
              Brak SFX. Wgraj plik audio (.mp3, .wav) — zostanie zmiksowany z wideo podczas
              renderowania.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {sfxFiles.map((sfx, i) => (
                <div
                  key={sfx.id}
                  className="flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06]"
                >
                  <Music size={9} className="text-green-400/50 shrink-0" />
                  <span className="text-[9px] text-white/50 truncate min-w-0 max-w-[100px]">
                    {sfx.fileName}
                  </span>
                  <label className="flex items-center gap-1 shrink-0">
                    <span className="text-[8px] text-white/25">Start</span>
                    <input
                      type="number"
                      value={sfx.startTime.toFixed(1)}
                      step={0.1}
                      min={0}
                      max={videoDuration}
                      onChange={(e) => updateSfxField(i, "startTime", Number(e.target.value))}
                      className="w-12 px-1 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
                    />
                    <span className="text-[8px] text-white/25">s</span>
                  </label>
                  <label className="flex items-center gap-1 shrink-0">
                    <Volume2 size={8} className="text-white/25" />
                    <input
                      type="range"
                      min={0}
                      max={2}
                      step={0.1}
                      value={sfx.volume}
                      onChange={(e) => updateSfxField(i, "volume", Number(e.target.value))}
                      className="w-16"
                    />
                    <span className="text-[8px] text-white/25">{sfx.volume.toFixed(1)}</span>
                  </label>
                  <button
                    onClick={() => deleteSfxFile(sfx.fileName)}
                    className="p-0.5 rounded text-white/20 hover:text-red-400 transition shrink-0 ml-auto"
                  >
                    <Trash2 size={9} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text style customization panel */}
      {showTextStylePanel && (
        <div className="shrink-0 border-t border-white/[0.06] bg-cyan-500/[0.03] px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
              Styl napisów
            </span>
            <button
              onClick={() => setShowTextStylePanel(false)}
              className="p-1 rounded text-white/20 hover:text-white/50 transition"
            >
              <X size={10} />
            </button>
          </div>

          {/* Preview */}
          <div className="rounded-lg bg-black/50 p-3 flex items-center justify-center mb-2">
            <div
              style={{
                padding: "4px 12px",
                borderRadius: 8,
                backgroundColor: captionCustom.bgColor,
                color: captionCustom.textColor,
                fontFamily: captionCustom.fontFamily,
                fontSize: captionCustom.fontSize,
                fontWeight: captionCustom.fontWeight,
                textTransform: captionCustom.textTransform as React.CSSProperties["textTransform"],
                textShadow: captionCustom.dropShadow
                  ? `0 2px ${captionCustom.shadowBlur}px ${captionCustom.shadowColor}`
                  : "none",
              }}
            >
              Przykładowy napis
            </div>
          </div>

          {/* Controls grid */}
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {/* Bg color */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Tło</span>
              <input
                type="color"
                value={captionCustom.bgColor.startsWith("rgba") ? "#000000" : captionCustom.bgColor}
                onChange={(e) => setCaptionCustom((prev) => ({ ...prev, bgColor: e.target.value }))}
                className="w-6 h-6 rounded cursor-pointer bg-transparent border-0"
              />
              <input
                type="text"
                value={captionCustom.bgColor}
                onChange={(e) => setCaptionCustom((prev) => ({ ...prev, bgColor: e.target.value }))}
                className="w-[120px] px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              />
            </label>

            {/* Text color */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Tekst</span>
              <input
                type="color"
                value={captionCustom.textColor}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({ ...prev, textColor: e.target.value }))
                }
                className="w-6 h-6 rounded cursor-pointer bg-transparent border-0"
              />
              <input
                type="text"
                value={captionCustom.textColor}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({ ...prev, textColor: e.target.value }))
                }
                className="w-[80px] px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              />
            </label>

            {/* Font */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Font</span>
              <select
                value={captionCustom.fontFamily}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({ ...prev, fontFamily: e.target.value }))
                }
                className="px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              >
                {FONT_OPTIONS.map((f) => (
                  <option key={f} value={f}>
                    {f.split(",")[0]}
                  </option>
                ))}
              </select>
            </label>

            {/* Font size */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Rozm.</span>
              <select
                value={captionCustom.fontSize}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({ ...prev, fontSize: e.target.value }))
                }
                className="px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              >
                {["0.8em", "0.9em", "1em", "1.1em", "1.15em", "1.2em", "1.3em", "1.5em"].map(
                  (s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  )
                )}
              </select>
            </label>

            {/* Font weight */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Waga</span>
              <select
                value={captionCustom.fontWeight}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({ ...prev, fontWeight: Number(e.target.value) }))
                }
                className="px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              >
                {[300, 400, 500, 600, 700, 800, 900].map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </label>

            {/* Text transform */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Caps</span>
              <select
                value={captionCustom.textTransform}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({
                    ...prev,
                    textTransform: e.target.value as CaptionCustomStyle["textTransform"],
                  }))
                }
                className="px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              >
                <option value="none">Normalny</option>
                <option value="uppercase">WIELKIE</option>
                <option value="lowercase">małe</option>
              </select>
            </label>

            {/* Drop shadow */}
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={captionCustom.dropShadow}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({ ...prev, dropShadow: e.target.checked }))
                }
                className="rounded w-3 h-3"
              />
              <span className="text-[9px] text-white/40">Cień</span>
            </label>

            {/* Entrance effect */}
            <label className="flex items-center gap-1.5">
              <span className="text-[9px] text-white/30 w-[30px]">Efekt</span>
              <select
                value={captionCustom.entranceEffect}
                onChange={(e) =>
                  setCaptionCustom((prev) => ({
                    ...prev,
                    entranceEffect: e.target.value as CaptionCustomStyle["entranceEffect"],
                  }))
                }
                className="px-1.5 py-0.5 text-[9px] bg-white/[0.06] border border-white/[0.08] rounded text-white/60"
              >
                {ENTRANCE_EFFECTS.map((ef) => (
                  <option key={ef.value} value={ef.value}>
                    {ef.label}
                  </option>
                ))}
              </select>
            </label>

            {/* Reset */}
            <button
              onClick={() => setCaptionCustom(DEFAULT_CAPTION_CUSTOM)}
              className="flex items-center gap-1 px-2 py-0.5 text-[9px] text-white/30 hover:text-white/50 transition"
            >
              <RotateCcw size={8} />
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="shrink-0 px-3 py-2">
        <VideoEditorSectionBoundary label="Timeline">
          <EditingTimeline
            duration={videoDuration}
            currentTime={currentTime}
            onSeek={handleSeek}
            subtitles={subtitles}
            overlays={scenePlan?.overlays ?? []}
            onSubtitleSelect={handleSubtitleSelect}
            onOverlaySelect={handleOverlaySelect}
            onSubtitleTimingChange={handleSubtitleTimingChange}
            onOverlayTimingChange={handleOverlayTimingChange}
            onSubtitleDelete={handleSubtitleDelete}
            onOverlayDelete={handleOverlayDelete}
            onSubtitleSplit={handleSubtitleSplit}
            onOverlaySplit={handleOverlaySplit}
            selectedSubtitleIndex={selectedSubtitleIndex}
            selectedOverlayIndex={selectedOverlayIndex}
            fileName={fileName}
          />
        </VideoEditorSectionBoundary>
      </div>
    </div>
  );
}
